<template>
    <BasePanel
        v-if="preEmptiveRightsCount > 0"
        :icon="TicketIcon"
        :icon-bold="true"
        :title="$t('portfolio.secondary_market_pre_emptive_rights_title', 'Secondary Market - Meine Bezugsrechte')"
        :loading="loading">
        <div class="mt-1 flex flex-col gap-3">
            <div class="flex items-baseline gap-2">
                <span class="text-3xl font-extrabold leading-none text-gray-900">
                    {{ preEmptiveRightsCount }}
                </span>
                <span class="text-sm font-medium text-emerald-700">
                    {{ $t('portfolio.rights_unit', 'Bezugsrechte') }}
                </span>
            </div>

            <div class="mt-1 divide-y divide-gray-100 border-b border-t border-gray-100 py-1">
                <div
                    v-for="asset in preEmptiveRightsAssets"
                    :key="asset.asset_id"
                    class="flex items-center justify-between py-2 text-sm">
                    <span class="font-semibold text-gray-800">
                        {{ asset.asset_title }}
                    </span>
                    <span class="font-bold text-emerald-600">
                        {{ asset.count }} {{ $t('portfolio.unit_short', 'Stk.') }}
                    </span>
                </div>
            </div>
        </div>
    </BasePanel>
</template>

<script setup lang="ts">
import { ref, watch, onMounted } from 'vue'
import BasePanel from '../../base/BasePanel.vue'
import { TicketIcon } from '@heroicons/vue/outline'
import { fetchBalance } from '../../../utilities/api'
import { AccountDataState } from '../../../typings'
import { usePersonalAccountDataState } from '../../../utilities/account/useAccountDataPersonal'
import { useLegalAccountDataState } from '../../../utilities/account/useAccountDataLegal'
import { isLegalProfile } from '../../../utilities/investment-process/useUserProfileType'

interface PreEmptiveAssetItem {
    asset_id: string
    asset_title: string
    count: number
}

const loading = ref<boolean>(false)
const accountState: AccountDataState = isLegalProfile() ? useLegalAccountDataState() : usePersonalAccountDataState()
const preEmptiveRightsCount = ref<number>(0)
const preEmptiveRightsAssets = ref<PreEmptiveAssetItem[]>([])

const loadPreEmptiveRights = async () => {
    if (!accountState.accountId) return

    loading.value = true
    try {
        const response = await fetchBalance(accountState.accountId)
        const balancesList = response.data?.balances || []

        const parsedAssets: PreEmptiveAssetItem[] = []
        let total = 0

        if (Array.isArray(balancesList)) {
            balancesList.forEach((item: any) => {
                const count = Number(item.balances?.pre_emptive || 0)
                if (count > 0) {
                    total += count
                    parsedAssets.push({
                        asset_id: item.asset_id,
                        asset_title: item.asset_title || 'Asset',
                        count: count,
                    })
                }
            })
        }

        preEmptiveRightsCount.value = total
        preEmptiveRightsAssets.value = parsedAssets
    } catch (error) {
        console.error('Failed to fetch pre-emptive rights balance:', error)
    } finally {
        loading.value = false
    }
}

watch(
    () => accountState.accountId,
    (newAccountId) => {
        if (newAccountId) {
            loadPreEmptiveRights()
        }
    },
    { immediate: true }
)

onMounted(async () => {
    if (!accountState.accountId) {
        await accountState.fetchAccountData()
    }
})
</script>
